/**
 * Bulleted list of ``IssuesChipEntry`` rows used inside the ``ExceptionDrawer``'s
 * Issues tab. Each collapsed row reads on one line: status rail, chevron,
 * severity icon, name, the lifecycle status pill, kebab - with a compact
 * "Marked fixed" progress stamp tucked under the title when marked fixed.
 * Expanding a row reveals the neutral type tag + lifecycle date, "Why this was
 * flagged" and "How to fix this issue". The focused row (the one the drawer was
 * opened from) auto-expands.
 *
 * Split per concern: row chrome lives in ``IssuesEntryRow``, the status-aware
 * kebab in ``IssuesRowKebab``, expanded detail bodies in
 * ``IssuesEntryDetail``. This file is the list mapper + shared prop types.
 */
import React from 'react'
import { Box } from '@mui/material'
import type { IssuesChipEntry } from './issuesState'
import type { RowAction } from './transitions'
import type { ExceptionDrawerDetailBlock } from '../types'
import IssuesEntryRow from './IssuesEntryRow'

export type { RowAction }

export interface IssuesEntryListProps {
  entries: IssuesChipEntry[]
  dense?: boolean
  // FP code for the lazy by-tag detail fetch. When present, non-primary
  // (sibling) rows that carry a ``tag_id`` become expandable to their own
  // Why + How (phase 3b). Absent in chip/tooltip contexts where the list
  // is read-only.
  fpCode?: string
  // When set, each tag-backed row gets a status-aware kebab of per-issue
  // overrides (the drawer's Issues list passes it for Evo/PSC). Off (default)
  // keeps the read-only chip/tooltip rendering.
  showRowActions?: boolean
  onRowAction?: (entry: IssuesChipEntry, action: RowAction) => void
  // The code the drawer was opened from. When set, the matching row is
  // auto-expanded on mount (the focus). Absent in read-only chip/tooltip
  // contexts AND in a no-focus interactive drawer (Data Explorer), where
  // nothing auto-expands.
  openedFromCode?: string
  // The drawer's interactive Issues list - makes every tag-backed row
  // expandable regardless of focus (so a no-focus drawer still expands on
  // click). Off in read-only chip/tooltip contexts.
  interactive?: boolean
  // Disable kebab items while a mutation is in flight.
  actionsPending?: boolean
  // Eager Why/How for the focused row. The envelope already carries the focused
  // code's ``detail_blocks`` (the common path on every drawer open), so the row
  // whose ``code === eagerDetailCode`` renders them directly instead of the lazy
  // by-tag fetch. Siblings (and FP, when no eager blocks ride) still fetch.
  eagerDetailCode?: string
  eagerDetailBlocks?: ExceptionDrawerDetailBlock[]
}

const IssuesEntryList: React.FC<IssuesEntryListProps> = ({
  entries,
  dense = false,
  fpCode,
  showRowActions = false,
  onRowAction,
  openedFromCode,
  interactive = false,
  actionsPending = false,
  eagerDetailCode,
  eagerDetailBlocks,
}) => {
  if (entries.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {entries.map((err, i) => (
        <IssuesEntryRow
          key={`${err.code}-${i}`}
          entry={err}
          index={i}
          dense={dense}
          fpCode={fpCode}
          showRowActions={showRowActions}
          onRowAction={onRowAction}
          openedFromCode={openedFromCode}
          interactive={interactive}
          actionsPending={actionsPending}
          eagerDetailCode={eagerDetailCode}
          eagerDetailBlocks={eagerDetailBlocks}
        />
      ))}
    </Box>
  )
}

export default IssuesEntryList
